import React, { createContext, useContext, useRef } from 'react'
import { API_BASE_URL } from '../config'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  email: string | null
  sendOtp: (email: string) => Promise<boolean>
  verifyOtp: (email: string, otp: string) => Promise<boolean>
  logout: () => void
  // Authenticated fetch used by every use*Service hook instead of hand-rolling
  // an Authorization header. On a 401 it transparently refreshes the access
  // token via /refresh-token and retries once before giving up.
  authFetch: (input: string, init?: RequestInit) => Promise<Response>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'auth_refresh_token'
const EMAIL_KEY = 'auth_email'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [token, setToken] = React.useState<string | null>(null)
  const [email, setEmail] = React.useState<string | null>(null)

  // Mirrors the state above in refs so authFetch — including its
  // retry-after-refresh path — always reads the latest token/refreshToken
  // from inside closures captured by in-flight fetches, without needing to
  // be recreated every time the token changes.
  const tokenRef = useRef<string | null>(null)
  const refreshTokenRef = useRef<string | null>(null)
  const emailRef = useRef<string | null>(null)
  // Dedupes concurrent 401s: if several requests expire at once, only the
  // first triggers a real /refresh-token call — the rest await this same
  // in-flight promise instead of racing to rotate the refresh token
  // themselves (rotation is single-use server-side, so a race would 401 the
  // loser and log the user out unnecessarily).
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null)

  const setAuthState = (newToken: string | null, newRefreshToken: string | null, newEmail: string | null) => {
    tokenRef.current = newToken
    refreshTokenRef.current = newRefreshToken
    emailRef.current = newEmail
    setToken(newToken)
    setEmail(newEmail)
    setIsAuthenticated(newToken !== null)
  }

  // Rehydrate auth state from localStorage on mount
  React.useEffect(() => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY)
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      const storedEmail = localStorage.getItem(EMAIL_KEY)

      if (storedToken && storedEmail) {
        setAuthState(storedToken, storedRefreshToken, storedEmail)
      }
    } catch (error) {
      console.error('Failed to read auth state from localStorage:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const sendOtp = async (email: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        return false
      }

      return true
    } catch (error) {
      console.error('Send OTP error:', error)
      return false
    }
  }

  const verifyOtp = async (email: string, otp: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      })

      if (!response.ok) {
        return false
      }

      const data = await response.json()

      try {
        localStorage.setItem(TOKEN_KEY, data.token)
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
        localStorage.setItem(EMAIL_KEY, data.email)
      } catch (error) {
        console.error('Failed to persist auth state to localStorage:', error)
      }

      setAuthState(data.token, data.refreshToken, data.email)
      return true
    } catch (error) {
      console.error('Verify OTP error:', error)
      return false
    }
  }

  const logout = () => {
    // Best-effort server-side revocation so a copied refresh token can't
    // outlive an explicit sign-out. Fire-and-forget: a failure here shouldn't
    // block clearing local state, since the access token is short-lived
    // anyway.
    const refreshToken = refreshTokenRef.current
    if (refreshToken) {
      fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {})
    }

    try {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(EMAIL_KEY)
      if (emailRef.current) localStorage.removeItem(`mc_terms_accepted_${emailRef.current}`)
    } catch (error) {
      console.error('Failed to clear auth state from localStorage:', error)
    }

    setAuthState(null, null, null)
  }

  // Exchanges the refresh token for a new access token, deduping concurrent
  // callers via refreshPromiseRef. Returns the new access token, or null if
  // the refresh token itself is invalid/expired — meaning the session can't
  // be silently renewed and the caller should fall back to logout().
  const performRefresh = (): Promise<string | null> => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = (async () => {
        const currentRefreshToken = refreshTokenRef.current
        if (!currentRefreshToken) {
          return null
        }
        try {
          const response = await fetch(`${API_BASE_URL}/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: currentRefreshToken }),
          })
          if (!response.ok) {
            return null
          }
          const data = await response.json()
          try {
            localStorage.setItem(TOKEN_KEY, data.token)
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
          } catch (error) {
            console.error('Failed to persist refreshed auth state to localStorage:', error)
          }
          tokenRef.current = data.token
          refreshTokenRef.current = data.refreshToken
          setToken(data.token)
          return data.token as string
        } catch (error) {
          console.error('Refresh token error:', error)
          return null
        }
      })().finally(() => {
        refreshPromiseRef.current = null
      })
    }
    return refreshPromiseRef.current
  }

  // Shared authenticated fetch. On a 401 (expired/invalid access token) it
  // transparently refreshes via /refresh-token and retries the request once;
  // only if the refresh token itself is no longer valid does it fall through
  // to logout(), which flips isAuthenticated and sends the user back to the
  // login screen (see App.tsx) instead of leaving failed requests to fail
  // silently or show a raw "401" error.
  const authFetch = async (input: string, init: RequestInit = {}): Promise<Response> => {
    const withAuthHeader = (bearerToken: string | null): RequestInit => ({
      ...init,
      headers: {
        ...(init.headers || {}),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      },
    })

    const response = await fetch(input, withAuthHeader(tokenRef.current))
    if (response.status !== 401 || !refreshTokenRef.current) {
      return response
    }

    const newToken = await performRefresh()
    if (!newToken) {
      logout()
      return response
    }

    return fetch(input, withAuthHeader(newToken))
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, token, email, sendOtp, verifyOtp, logout, authFetch }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
